// Local-disk interchange for scripts (spec §R8, §6.2).
//
// R8 byte-clean rules: store and export scripts byte-for-byte as typed — UTF-8,
// no BOM, no headers/footers/metadata injected. We normalize to "\n" internally
// (stripping a BOM and CRLF on upload) and emit "\n" on download, so a file saved
// here opens and runs unmodified in Thonny / on the robot, and vice-versa.

// Leading UTF-8 BOM (U+FEFF), written as an escape so the source stays ASCII.
const BOM = new RegExp("^\\uFEFF");

/**
 * Read a Blob/File as UTF-8 text. Uses `.text()` where available (browsers) and
 * falls back to FileReader (jsdom, older engines) so it works everywhere.
 */
export function blobText(blob: Blob): Promise<string> {
  if (typeof blob.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/** Ensure a script name ends in `.py` (Save/Save As require it, §6.2). */
export function ensurePyName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "untitled.py";
  return /\.py$/i.test(trimmed) ? trimmed : `${trimmed}.py`;
}

/**
 * The exact editor buffer as a downloadable blob: UTF-8, no BOM, nothing
 * injected. Passing the string straight to Blob keeps the bytes verbatim.
 */
export function makeScriptBlob(code: string): Blob {
  return new Blob([code], { type: "text/x-python;charset=utf-8" });
}

/** Browser download of the current buffer as `<name>.py`. */
export function downloadScript(name: string, code: string): void {
  const url = URL.createObjectURL(makeScriptBlob(code));
  const a = document.createElement("a");
  a.href = url;
  a.download = ensurePyName(name);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Read an uploaded `.py`/`.txt` into the editor buffer: strip a UTF-8 BOM a
 * Windows editor may have added and normalize CRLF -> LF, so the internal buffer
 * is always LF (R8). No other transformation — the code is otherwise verbatim.
 */
export async function readScriptFile(file: File): Promise<string> {
  const text = await blobText(file);
  return text.replace(BOM, "").replace(/\r\n/g, "\n");
}
